import { DEFAULT_STATE_CODE, getState as getJurisdiction } from "../data/states.js";
import {
  amountFinanced,
  amountFinancedForDown,
  computeDealGross,
  computeTax,
  financeCharge,
  monthlyPayment,
  roundMoney,
  taxableBase,
} from "./calc.js";

const listeners = new Set();

function initialDealCosts() {
  return {
    vehicleCost: 24500,
    feeCost: 180,
    productCost: 980,
  };
}

function createInitialState() {
  const jurisdiction = getJurisdiction(DEFAULT_STATE_CODE);
  const price = 28995;
  const fees = jurisdiction.defaultFees;
  const insGap = 895;
  const accessories = 499;
  const vsc = 2099;
  const down = 2000;
  const trade = 1500;
  const tax = roundMoney(
    computeTax(taxableBase({ price, accessories, trade }), jurisdiction.taxRate)
  );
  const dealCosts = initialDealCosts();
  const productProfit = roundMoney(
    insGap + vsc + accessories - dealCosts.productCost
  );

  return {
    stateCode: jurisdiction.code,
    worksheetId: `PD-${jurisdiction.code}-${Date.now().toString(36).toUpperCase()}`,
    structure: {
      price,
      fees,
      insGap,
      accessories,
      vsc,
      tax,
      taxLocked: true, // always computed from effective rate
      taxManual: false, // true when user set rates for this deal only
      customStateTaxRate: null, // percent, e.g. 6.25
      customLocalTaxRate: null, // percent
      down,
      trade,
      frequencyPreset: "48", // "12" | "24" | "36" | "48" | "custom"
      frequencyMonths: 48,
      firstPaymentDays: 30,
      buyRate: 8.99,
    },
    downs: [1000, 2000, 3000],
    terms: [
      { months: 36, apr: 15 },
      { months: 48, apr: 15 },
      { months: 60, apr: 18.25 },
    ],
    selection: { downIndex: 1, termIndex: 1 },
    dealCosts,
    productProfit,
    commissions: 450,
    customer: {
      name: "",
      phone: "",
      email: "",
      stock: "DEMO-1042",
      year: "2022",
      make: "Toyota",
      model: "Camry SE",
      vin: "",
      color: "",
      body: "",
      mileage: "",
    },
    ui: {
      printQuoteOpen: false,
      printSummaryOpen: false,
      formsPackOpen: false,
      taxRatesOpen: false,
      dealCostsOpen: false,
      printDisclaimerOpen: false,
      printDisclaimerPending: null, // "quote" | "summary" | "forms-packet" | "form-one"
      printVersion: "customer",
      printTemplate: "classic", // "classic" | "grid"
      showCost: true,
      showFrontGross: true,
      showBackGross: true,
      showNetProfit: true,
      showTradeCredit: true,
      showApr: true,
      showFinanceCharge: true,
      showAmountFinanced: true,
      showDealStructure: true,
      /** Persisted form checkbox ids for current state (session only). */
      selectedFormIds: null,
      formsSelectionState: null,
      /** Super-user disabled form ids (localStorage-backed). */
      disabledFormIds: null,
    },
  };
}

/** Combined effective tax rate as a decimal (e.g. 0.082). Deal-only override when taxManual. */
export function resolveTaxRateDecimal(structure, stateCode = appState.stateCode) {
  if (structure?.taxManual) {
    const statePct = Number(structure.customStateTaxRate) || 0;
    const localPct = Number(structure.customLocalTaxRate) || 0;
    return Math.max(0, (statePct + localPct) / 100);
  }
  return Number(getJurisdiction(stateCode).taxRate) || 0;
}

export function resolveTaxRatePercent(structure, stateCode = appState.stateCode) {
  return roundMoney(resolveTaxRateDecimal(structure, stateCode) * 100);
}

function recalcStructureTax(structure, stateCode = appState.stateCode) {
  const next = { ...structure };
  next.tax = roundMoney(
    computeTax(
      taxableBase({
        price: next.price,
        accessories: next.accessories,
        trade: next.trade,
      }),
      resolveTaxRateDecimal(next, stateCode)
    )
  );
  return next;
}

let appState = createInitialState();

export function getAppState() {
  return appState;
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  const snapshot = appState;
  listeners.forEach((fn) => fn(snapshot));
}

export function resolveFrequencyMonths(structure = appState.structure) {
  const preset = String(structure.frequencyPreset || "48");
  if (preset === "custom") {
    return Math.max(1, Math.round(Number(structure.frequencyMonths) || 0));
  }
  const n = Number(preset);
  return Number.isFinite(n) && n > 0 ? n : 48;
}

export function updateStructure(patch) {
  const structure = { ...appState.structure, ...patch };
  const moneyKeys = [
    "price",
    "fees",
    "insGap",
    "accessories",
    "vsc",
    "down",
    "trade",
    "buyRate",
  ];
  for (const key of moneyKeys) {
    if (Object.prototype.hasOwnProperty.call(patch, key)) {
      structure[key] = roundMoney(patch[key]);
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, "frequencyPreset")) {
    const preset = String(patch.frequencyPreset);
    structure.frequencyPreset = preset;
    if (preset !== "custom") {
      structure.frequencyMonths = Number(preset) || 48;
    } else if (!Number(structure.frequencyMonths)) {
      structure.frequencyMonths = 60;
    }
  }
  if (Object.prototype.hasOwnProperty.call(patch, "frequencyMonths")) {
    structure.frequencyMonths = Math.max(
      1,
      Math.round(Number(patch.frequencyMonths) || 0)
    );
    const known = new Set(["12", "24", "36", "48"]);
    if (!known.has(String(structure.frequencyMonths))) {
      structure.frequencyPreset = "custom";
    } else if (structure.frequencyPreset !== "custom") {
      structure.frequencyPreset = String(structure.frequencyMonths);
    }
  }
  if (Object.prototype.hasOwnProperty.call(patch, "firstPaymentDays")) {
    structure.firstPaymentDays = Math.max(
      0,
      Math.round(Number(patch.firstPaymentDays) || 0)
    );
  }

  // Total Tax always follows the effective rate (state default or deal override)
  Object.assign(structure, recalcStructureTax(structure, appState.stateCode));

  // Only recompute Back Gross when product revenue lines change (not buy rate, days, etc.)
  const productKeys = ["insGap", "vsc", "accessories"];
  const productTouched = productKeys.some((k) =>
    Object.prototype.hasOwnProperty.call(patch, k)
  );
  let productProfit = appState.productProfit;
  if (productTouched) {
    const productRevenue =
      Number(structure.insGap) +
      Number(structure.vsc) +
      Number(structure.accessories);
    productProfit = roundMoney(
      productRevenue - Number(appState.dealCosts.productCost)
    );
  }

  appState = { ...appState, structure, productProfit };
  if (Object.prototype.hasOwnProperty.call(patch, "down")) {
    const downs = [...appState.downs];
    downs[appState.selection.downIndex] = structure.down;
    appState = { ...appState, downs };
  }
  emit();
}

/** Apply custom tax rates for this deal only (not persisted across visits). */
export function applyDealTaxRates({ stateRatePercent, localRatePercent }) {
  const statePct = Math.max(0, Number(stateRatePercent) || 0);
  const localPct = Math.max(0, Number(localRatePercent) || 0);
  const combinedPct = roundMoney(statePct + localPct);
  const jurisdiction = getJurisdiction(appState.stateCode);
  const defaultPct = roundMoney((Number(jurisdiction.taxRate) || 0) * 100);
  // Combined rate matching the jurisdiction default is not a manual override
  // (including state/local splits that add up to the same total).
  if (Math.abs(combinedPct - defaultPct) < 0.005) {
    resetDealTaxToDefault();
    return;
  }
  const structure = recalcStructureTax(
    {
      ...appState.structure,
      taxManual: true,
      customStateTaxRate: statePct,
      customLocalTaxRate: localPct,
    },
    appState.stateCode
  );
  appState = { ...appState, structure, ui: { ...appState.ui, taxRatesOpen: false } };
  emit();
}

/** Restore jurisdiction default tax rate for this deal. */
export function resetDealTaxToDefault() {
  const structure = recalcStructureTax(
    {
      ...appState.structure,
      taxManual: false,
      customStateTaxRate: null,
      customLocalTaxRate: null,
    },
    appState.stateCode
  );
  appState = { ...appState, structure, ui: { ...appState.ui, taxRatesOpen: false } };
  emit();
}

export function setJurisdiction(code) {
  const jurisdiction = getJurisdiction(code);
  // Changing state clears any deal-only tax override and form picks
  const structure = recalcStructureTax(
    {
      ...appState.structure,
      fees: jurisdiction.defaultFees,
      taxManual: false,
      customStateTaxRate: null,
      customLocalTaxRate: null,
    },
    jurisdiction.code
  );
  appState = {
    ...appState,
    stateCode: jurisdiction.code,
    worksheetId: `PD-${jurisdiction.code}-${Date.now().toString(36).toUpperCase()}`,
    structure,
    ui: {
      ...appState.ui,
      selectedFormIds: null,
      formsSelectionState: null,
    },
  };
  emit();
}

export function setSelectedFormIds(ids) {
  const list = Array.isArray(ids) ? [...ids] : [];
  appState = {
    ...appState,
    ui: {
      ...appState.ui,
      selectedFormIds: list,
      formsSelectionState: appState.stateCode,
    },
  };
  emit();
}

/** Full worksheet reset — same as a fresh visit / new quote. */
export function startNewQuote() {
  appState = createInitialState();
  emit();
}

export function updateDown(index, value) {
  const downs = [...appState.downs];
  downs[index] = roundMoney(value);
  const next = { ...appState, downs };
  if (index === appState.selection.downIndex) {
    next.structure = { ...appState.structure, down: downs[index] };
  }
  appState = next;
  emit();
}

export function updateTerm(index, patch) {
  const nextPatch = { ...patch };
  if (nextPatch.apr != null) nextPatch.apr = roundMoney(nextPatch.apr);
  if (nextPatch.months != null) {
    nextPatch.months = Math.max(1, Math.round(Number(nextPatch.months) || 0));
  }
  const terms = appState.terms.map((t, i) =>
    i === index ? { ...t, ...nextPatch } : t
  );
  appState = { ...appState, terms };
  emit();
}

export function selectCell(downIndex, termIndex) {
  const down = appState.downs[downIndex] ?? appState.structure.down;
  appState = {
    ...appState,
    selection: { downIndex, termIndex },
    structure: {
      ...appState.structure,
      down,
    },
  };
  emit();
}

export function updateDealCosts(patch) {
  const dealCosts = { ...appState.dealCosts };
  for (const [key, value] of Object.entries(patch)) {
    dealCosts[key] = roundMoney(value);
  }
  // Only recompute Back Gross when Product Cost changes (preserve manual override otherwise)
  let productProfit = appState.productProfit;
  if (Object.prototype.hasOwnProperty.call(patch, "productCost")) {
    const productRevenue =
      Number(appState.structure.insGap) +
      Number(appState.structure.vsc) +
      Number(appState.structure.accessories);
    productProfit = roundMoney(
      productRevenue - Number(dealCosts.productCost)
    );
  }
  appState = { ...appState, dealCosts, productProfit };
  emit();
}

export function setProductProfit(value) {
  appState = { ...appState, productProfit: roundMoney(value) };
  emit();
}

export function setCommissions(value) {
  appState = { ...appState, commissions: roundMoney(value) };
  emit();
}

export function updateCustomer(patch) {
  appState = { ...appState, customer: { ...appState.customer, ...patch } };
  emit();
}

export function updateUi(patch) {
  appState = { ...appState, ui: { ...appState.ui, ...patch } };
  emit();
}

export function derived(state = appState) {
  const jurisdiction = getJurisdiction(state.stateCode);
  const s = state.structure;
  const afStructure = roundMoney(amountFinanced(s));
  const selectedDown = state.downs[state.selection.downIndex] ?? s.down;
  const selectedTerm = state.terms[state.selection.termIndex] || state.terms[0];
  const afSelected = roundMoney(
    amountFinancedForDown({ ...s, down: selectedDown }, selectedDown)
  );
  const payment = roundMoney(
    monthlyPayment(afSelected, selectedTerm.apr, selectedTerm.months)
  );
  const fc = roundMoney(financeCharge(afSelected, payment, selectedTerm.months));

  const matrix = state.downs.map((down) => {
    const af = roundMoney(amountFinancedForDown({ ...s, down }, down));
    return state.terms.map((term) => {
      const pmt = roundMoney(monthlyPayment(af, term.apr, term.months));
      return {
        amountFinanced: af,
        payment: pmt,
        financeCharge: roundMoney(financeCharge(af, pmt, term.months)),
      };
    });
  });

  const dealCostsTotal =
    Number(state.dealCosts.vehicleCost) +
    Number(state.dealCosts.feeCost) +
    Number(state.dealCosts.productCost);

  const gross = computeDealGross({
    price: s.price,
    vehicleCost: state.dealCosts.vehicleCost,
    feeCost: state.dealCosts.feeCost,
    productProfit: state.productProfit,
    commissions: state.commissions,
  });

  const frequencyMonths = resolveFrequencyMonths(s);
  // Left-column Monthly Payment uses Buy Rate × Frequency (independent of payment grid).
  const structurePayment = roundMoney(
    monthlyPayment(afStructure, s.buyRate, frequencyMonths)
  );
  const structureFinanceCharge = roundMoney(
    financeCharge(afStructure, structurePayment, frequencyMonths)
  );

  const taxRateDecimal = resolveTaxRateDecimal(s, state.stateCode);
  const taxBase = roundMoney(
    taxableBase({
      price: s.price,
      accessories: s.accessories,
      trade: s.trade,
    })
  );

  return {
    jurisdiction,
    amountFinanced: afStructure,
    selectedAmountFinanced: afSelected,
    selectedDown,
    selectedTerm,
    selectedPayment: payment,
    financeCharge: fc,
    frequencyMonths,
    structurePayment,
    structureFinanceCharge,
    matrix,
    dealCostsTotal: roundMoney(dealCostsTotal),
    frontGross: roundMoney(gross.frontGross),
    backGross: roundMoney(gross.backGross),
    totalGross: roundMoney(gross.totalGross),
    netProfit: roundMoney(gross.netProfit),
    taxManual: !!s.taxManual,
    taxRateDecimal,
    taxRatePercent: roundMoney(taxRateDecimal * 100),
    taxableBaseAmount: taxBase,
  };
}

export function resetDemo() {
  startNewQuote();
}

/** Fine-print flag for printed paperwork when tax was manually entered. */
export function taxManualPrintNote(state = appState) {
  if (!state?.structure?.taxManual) return "";
  const jurisdiction = getJurisdiction(state.stateCode);
  const defaultPct = roundMoney((Number(jurisdiction.taxRate) || 0) * 100);
  const currentPct = resolveTaxRatePercent(state.structure, state.stateCode);
  // No footnote when the effective rate still matches the state default.
  if (Math.abs(currentPct - defaultPct) < 0.005) return "";
  return "*This tax rate has been manually entered";
}
